'use client'

import { useEffect, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SamanthaStep {
  key: string
  label: string
  emoji?: string
}

interface SamanthaStepsProps {
  steps: SamanthaStep[]
  intervalMs?: number
  onComplete?: () => void
  onStepChange?: (step: SamanthaStep, index: number, total: number) => void
  compact?: boolean
}

export function SamanthaSteps({
  steps,
  intervalMs = 900,
  onComplete,
  onStepChange,
  compact = false,
}: SamanthaStepsProps) {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!steps?.length) return

    setActiveIndex(0)
    onStepChange?.(steps[0], 0, steps.length)

    const timers: ReturnType<typeof setTimeout>[] = []

    steps.forEach((_, i) => {
      timers.push(
        setTimeout(() => {
          const next = i + 1
          setActiveIndex(next)

          if (next < steps.length) {
            onStepChange?.(steps[next], next, steps.length)
          }

          if (next >= steps.length) {
            onComplete?.()
          }
        }, intervalMs * (i + 1))
      )
    })

    return () => timers.forEach(clearTimeout)
  }, [steps, intervalMs, onComplete, onStepChange])

  if (!steps?.length) return null

  return (
    <div
      className={cn(
        'space-y-1.5',
        !compact && 'rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/70 p-3'
      )}
    >
      {steps.map((step, i) => {
        const done = i < activeIndex
        const active = i === activeIndex
        const pending = i > activeIndex

        return (
          <div
            key={`${step.key}-${i}`}
            className={cn(
              'flex items-center gap-2 text-xs transition-all duration-300',
              pending && 'opacity-40',
              active && 'opacity-100',
              done && 'opacity-90'
            )}
          >
            <div
              className={cn(
                'h-4 w-4 rounded-full flex items-center justify-center shrink-0 transition-all',
                done && 'bg-emerald-500 text-white',
                active && 'bg-black/10 dark:bg-white/10 text-black dark:text-white',
                pending && 'bg-neutral-200 dark:bg-neutral-800 text-neutral-500'
              )}
            >
              {done ? (
                <Check className="h-2.5 w-2.5" />
              ) : active ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
              )}
            </div>

            <span
              className={cn(
                'leading-tight',
                done && 'text-neutral-900 dark:text-neutral-100',
                active && 'text-neutral-900 dark:text-neutral-100 font-medium',
                pending && 'text-neutral-500 dark:text-neutral-400'
              )}
            >
              {step.emoji ? `${step.emoji} ` : ''}
              {step.label}
            </span>
          </div>
        )
      })}
    </div>
  )
}